import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  House,
  Users,
  Buildings,
  Target,
  Folder,
  FileText,
  CurrencyDollar,
  CheckSquare,
  Handshake,
  ChartBar,
  Gear,
  SignOut,
  List,
  CaretDown,
  Bell,
  Plus,
  MagnifyingGlass,
  Lightning,
  Eye,
  CheckCircle,
  Warning,
  Info,
  X,
  UsersThree,
  ClockCounterClockwise,
  FloppyDisk,
  RocketLaunch,
  Moon,
  Sun,
  MapPin,
  CalendarBlank,
  Globe,
  WifiHigh,
  WifiSlash,
  Wallet,
  FlowArrow,
  Coin,
  Scales,
  PaperPlaneTilt,
  EnvelopeSimple,
  Receipt,
  Percent,
  BookOpen,
  ChartLine,
  ChatCircleDots
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { getNotifications, markNotificationRead, markAllNotificationsRead, getNotificationsCount, runAutomations, resetMyTourProgress, getContacts } from '../services/api';
import { toast } from 'sonner';
import { useWebSocket } from '../hooks/useWebSocket';
import { OrgSelector } from './OrgSelector';
import { Breadcrumbs } from './Breadcrumbs';
import { AppTour } from './AppTour';
import { TourProgressBar } from './TourProgressBar';
import { BackButton } from './BackButton';
import ProfileCompletionWizard from './ProfileCompletionWizard';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';

const menuItems = [
  { path: '/dashboard', label: 'Tableau de bord', icon: House },
  { path: '/oto', label: 'OTO · Assistant IA', icon: ChatCircleDots, otoOnly: true },
  { path: '/contacts', label: 'Contacts', icon: Users },
  { path: '/organizations', label: 'Organisations', icon: Buildings },
  { path: '/opportunities', label: 'Opportunites', icon: Target },
  { path: '/dossiers', label: 'Dossiers ESS', icon: Folder },
  { path: '/documents', label: 'GED ESS', icon: FileText },
  { path: '/ged/registers', label: 'Registres GED', icon: Folder },
  { path: '/payments', label: 'Paiements', icon: CurrencyDollar },
  { path: '/paiements/envoi', label: 'Envoi paiement', icon: PaperPlaneTilt },
  { path: '/factures', label: 'Factures', icon: Receipt },
  { path: '/factures/tva', label: 'Paramètres TVA', icon: Percent, adminOnly: true },
  { path: '/finance', label: 'Finances', icon: ChartBar },
  { path: '/tasks', label: 'Taches', icon: CheckSquare },
  { path: '/partners', label: 'Partenaires', icon: Handshake },
  { path: '/reporting', label: 'Reporting', icon: ChartBar },
  { path: '/vote-simulation', label: 'Simulation vote AG', icon: Scales },
  { path: '/territoires', label: 'Territoires', icon: MapPin },
  { path: '/kpi', label: 'Suivi KPI', icon: Target },
  { path: '/calendrier', label: 'Calendrier', icon: CalendarBlank },
  { path: '/impact-ess', label: 'Impact ESS', icon: Globe },
  { path: '/impact-org', label: 'Impact Org', icon: Buildings },
  { path: '/workflows', label: 'Automatisations', icon: FlowArrow },
  { path: '/wallet', label: 'Wallet & Credits', icon: Wallet },
  { path: '/cmc', label: "CMC O'SCOP", icon: Coin },
  { path: '/emails/composer', label: 'Composer un mail', icon: PaperPlaneTilt, adminOnly: true },
  { path: '/publications', label: 'Publications', icon: PaperPlaneTilt, adminOnly: true },
  { path: '/espaces', label: 'Espaces partagés', icon: UsersThree, adminOnly: true },
  { path: '/adhesion', label: 'Kits adhésion', icon: Handshake, adminOnly: true },
  { path: '/emails/segments', label: 'Segments ciblés', icon: UsersThree, adminOnly: true },
  { path: '/emails/notifications', label: 'Notifications', icon: EnvelopeSimple, adminOnly: true },
  { path: '/emails/journal', label: 'Journal des emails', icon: EnvelopeSimple, adminOnly: true },
  { path: '/users', label: 'Utilisateurs', icon: UsersThree, adminOnly: true },
  { path: '/activity', label: 'Journal', icon: ClockCounterClockwise, adminOnly: true },
  { path: '/backup', label: 'Sauvegardes', icon: FloppyDisk, adminOnly: true },
  { path: '/admin/landing', label: 'Landing publique', icon: Globe, adminOnly: true },
  { path: '/admin/landing-ab', label: 'Landing A/B', icon: ChartLine, adminOnly: true },
  { path: '/admin/templates', label: 'Modèles missions', icon: BookOpen, adminOnly: true },
  { path: '/admin/portal-resources', label: 'Ressources portail', icon: BookOpen, adminOnly: true },
  { path: '/admin/missions', label: 'Missions publiques', icon: Handshake, adminOnly: true },
  { path: '/admin/tour', label: 'Tour onboarding', icon: RocketLaunch, adminOnly: true },
  { path: '/onboarding', label: 'Onboarding FTPE', icon: RocketLaunch, adminOnly: true },
  { path: '/search', label: 'Recherche', icon: MagnifyingGlass },
  { path: '/settings', label: 'Parametres', icon: Gear },
];

function SidebarNav({ items, user, location, collapsed, pendingContacts = 0 }) {
  return (
    <nav className="flex-1 py-4 overflow-y-auto">
      <ul className="space-y-1 px-2">
        {items.map((item) => {
          if (item.otoOnly && !['super_admin', 'admin', 'direction'].includes(user?.role)) return null;
          if (item.adminOnly && user?.role !== 'super_admin' && user?.role !== 'direction') return null;
          const Icon = item.icon;
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const showBadge = item.path === '/contacts' && pendingContacts > 0;
          return (
            <li key={item.path}>
              <Link
                to={item.path}
                data-testid={`nav-${item.path.slice(1).replace(/\//g, '-')}`}
                className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-[#2D5A4C] to-[#1E4136] text-white shadow-lg shadow-[#2D5A4C]/25'
                    : 'text-[#5E6D66] hover:bg-white/70 hover:text-[#1A2421] hover:shadow-sm hover:translate-x-0.5'
                }`}
              >
                {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-[#80C51F] shadow-[0_0_10px_rgba(128,197,31,0.6)]" />}
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} className={isActive ? '' : 'group-hover:scale-110 transition-transform'} />
                {!collapsed && <span className="text-sm font-medium tracking-[0.01em]">{item.label}</span>}
                {showBadge && (
                  <span
                    className={`${collapsed ? 'absolute -top-1 left-4' : 'ml-auto'} min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF8E00] text-white text-[10px] font-bold flex items-center justify-center shadow-md animate-pulse`}
                    data-testid="nav-contacts-pending-badge"
                    title={`${pendingContacts} contact(s) « à compléter » en attente`}
                  >
                    {pendingContacts > 99 ? '99+' : pendingContacts}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function AccentBar() {
  return (
    <div className="flex gap-0.5 px-4 py-2">
      <div className="h-0.5 flex-1 rounded-full" style={{ background: '#00AEEF' }} />
      <div className="h-0.5 flex-1 rounded-full" style={{ background: '#80C51F' }} />
      <div className="h-0.5 flex-1 rounded-full" style={{ background: '#EA3C3A' }} />
      <div className="h-0.5 flex-1 rounded-full" style={{ background: '#FF8E00' }} />
      <div className="h-0.5 flex-1 rounded-full" style={{ background: '#D5007D' }} />
    </div>
  );
}

export function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, wsToken } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(false);
  const [pendingContactsCount, setPendingContactsCount] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const notifSoundRef = useRef(null);

  // Ouvre le wizard de complétion de profil au premier login des prospects non complétés
  useEffect(() => {
    if (!user) return;
    const dismissed = typeof window !== 'undefined' ? localStorage.getItem('oscop_profile_wizard_dismissed') : null;
    if (user.role === 'prospect' && user.profile_completed === false && !dismissed) {
      setWizardOpen(true);
    }
  }, [user]);

  const fetchPendingContacts = useCallback(async () => {
    try {
      const res = await getContacts({ auto_created: true, limit: 1 });
      setPendingContactsCount(res.data?.total || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchPendingContacts();
    const t = setInterval(fetchPendingContacts, 60000);
    return () => clearInterval(t);
  }, [fetchPendingContacts, location.pathname]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getNotificationsCount();
      setUnreadCount(res.data.unread_count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications(false, 20);
      setNotifications(res.data.items);
      setUnreadCount(res.data.unread_count);
    } catch {}
  }, []);

  // WebSocket real-time notifications with enriched toasts
  const handleWsNotification = useCallback((notifData) => {
    setNotifications(prev => [notifData, ...prev.slice(0, 19)]);
    setUnreadCount(prev => prev + 1);
    const severityMap = { warning: 'warning', error: 'error' };
    const toastType = severityMap[notifData.severity] || 'info';
    const linkPath = notifData.link || null;
    const toastOpts = {
      description: notifData.message,
      duration: 5000,
      ...(linkPath && {
        action: {
          label: 'Voir',
          onClick: () => navigate(linkPath),
        },
      }),
    };
    if (toastType === 'warning') {
      toast.warning(notifData.title, toastOpts);
    } else if (toastType === 'error') {
      toast.error(notifData.title, toastOpts);
    } else {
      toast.info(notifData.title, toastOpts);
    }
  }, [navigate]);

  const { connected: wsConnected } = useWebSocket({
    onNotification: handleWsNotification,
    enabled: !!user,
    token: wsToken,
  });

  // Initial load + fallback polling (60s instead of 30s since we have WS)
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifOpen, fetchNotifications]);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleRunAutomations = async () => {
    setRunningAutomation(true);
    try {
      const res = await runAutomations();
      const count = res.data.new_notifications;
      toast.success(`Scan termine: ${count} nouvelle(s) alerte(s)`);
      fetchNotifications();
    } catch (err) {
      toast.error('Erreur lors du scan automatique');
    } finally {
      setRunningAutomation(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'warning': return <Warning size={14} className="text-amber-500" />;
      case 'error': return <Warning size={14} className="text-red-500" />;
      default: return <Info size={14} className="text-blue-500" />;
    }
  };

  const getTimeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'A l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return `Il y a ${Math.floor(diff / 86400)}j`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const UserMenuSection = ({ showLabels }) => (
    <div>
      <TourProgressBar collapsed={!showLabels} />
      <div className="p-4 border-t border-[#E8E6E1]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button 
            className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-[#F9F8F5] transition-colors"
            data-testid="user-menu-trigger"
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-[#8DB0A1] text-white text-xs">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            {showLabels && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-[#1A2421] truncate">{user?.name}</p>
                  <p className="text-xs text-[#5E6D66] capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <CaretDown size={16} className="text-[#5E6D66]" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Gear size={16} className="mr-2" />
            Paramètres
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              try {
                await resetMyTourProgress();
                try { localStorage.removeItem('oscop_tour_seen_v1'); } catch {}
                window.dispatchEvent(new CustomEvent('oscop:tour:open', { detail: { index: 0 } }));
              } catch { /* silencieux */ }
            }}
            data-testid="replay-tour-btn"
          >
            <RocketLaunch size={16} className="mr-2" />
            Revoir le tour d'accueil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-[#D47A62]" data-testid="logout-btn">
            <SignOut size={16} className="mr-2" />
            Déconnexion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F9F8F5]">
      {/* Mini-tour au premier login */}
      <AppTour />
      <ProfileCompletionWizard
        open={wizardOpen}
        onOpenChange={(v) => {
          setWizardOpen(v);
          if (!v) {
            try { localStorage.setItem('oscop_profile_wizard_dismissed', '1'); } catch {}
          }
        }}
        onCompleted={() => {
          try { localStorage.removeItem('oscop_profile_wizard_dismissed'); } catch {}
        }}
      />
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-backdrop"
        />
      )}

      {/* Desktop sidebar */}
      <aside
        className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-[#E8E6E1] flex-col transition-all duration-200 fixed h-full z-40 hidden md:flex`}
        data-testid="desktop-sidebar"
      >
        <div className="h-16 flex items-center px-4 border-b border-[#E8E6E1]">
          {!sidebarCollapsed ? (
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <img src={LOGO_URL} alt="Objectif SCOP" className="w-9 h-9 object-contain flex-shrink-0" data-testid="sidebar-logo" />
              <div className="min-w-0">
                <span className="font-semibold text-[#1A2421] text-sm block truncate" style={{ fontFamily: 'Manrope' }}>Objectif SCOP</span>
                <span className="text-[10px] text-[#5E6D66] block">Outremer</span>
              </div>
            </div>
          ) : (
            <img src={LOGO_URL} alt="OS" className="w-8 h-8 object-contain mx-auto" data-testid="sidebar-logo-collapsed" />
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`${sidebarCollapsed ? 'mx-auto mt-0' : 'ml-auto'} p-1.5 hover:bg-[#F9F8F5] rounded-lg transition-colors`}
            data-testid="sidebar-toggle"
          >
            <List size={20} className="text-[#5E6D66]" />
          </button>
        </div>
        <SidebarNav items={menuItems} user={user} location={location} collapsed={sidebarCollapsed} pendingContacts={pendingContactsCount} />
        {!sidebarCollapsed && <AccentBar />}
        <UserMenuSection showLabels={!sidebarCollapsed} />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
      <aside
        className="fixed inset-y-0 left-0 w-72 max-w-[calc(100vw_-_2rem)] overflow-x-hidden bg-white border-r border-[#E8E6E1] flex flex-col z-50 md:hidden animate-in fade-in duration-200"
        data-testid="mobile-sidebar"
      >
        <div className="h-16 flex items-center px-4 border-b border-[#E8E6E1]">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <img src={LOGO_URL} alt="Objectif SCOP" className="w-9 h-9 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-semibold text-[#1A2421] text-sm block truncate" style={{ fontFamily: 'Manrope' }}>Objectif SCOP</span>
              <span className="text-[10px] text-[#5E6D66] block">Outremer</span>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="ml-auto p-1.5 hover:bg-[#F9F8F5] rounded-lg transition-colors"
            data-testid="mobile-sidebar-close"
          >
            <X size={20} className="text-[#5E6D66]" />
          </button>
        </div>
        <SidebarNav items={menuItems} user={user} location={location} collapsed={false} pendingContacts={pendingContactsCount} />
        <AccentBar />
        <UserMenuSection showLabels={true} />
      </aside>
      )}

      {/* Main content */}
      <div className={`flex-1 min-w-0 transition-all duration-200 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Header */}
        <header className="h-14 md:h-16 bg-white/80 backdrop-blur-xl border-b border-[#E8E6E1] sticky top-0 z-30 flex items-center justify-between px-3 md:px-6">
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            {/* Back to previous page */}
            <BackButton fallback="/dashboard" testId="header-back-btn" />

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors md:hidden"
              data-testid="mobile-menu-btn"
            >
              <List size={22} className="text-[#5E6D66]" />
            </button>

            <div className="relative max-w-md flex-1 min-w-0 hidden lg:block">
              <button
                onClick={() => {
                  const e = new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true });
                  window.dispatchEvent(e);
                }}
                className="flex items-center w-full pl-10 pr-12 py-2 bg-[#F9F8F5] rounded-md text-sm text-[#5E6D66] hover:bg-[#F0EFEB] transition-colors cursor-pointer border border-transparent hover:border-[#E8E6E1]"
                data-testid="global-search"
              >
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
                Rechercher...
                <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-mono font-medium text-[#8DB0A1] bg-white rounded border border-[#E8E6E1]">
                  Ctrl+K
                </kbd>
              </button>
            </div>

            {/* Mobile search */}
            <button
              onClick={() => {
                const e = new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true });
                window.dispatchEvent(e);
              }}
              className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors lg:hidden"
              data-testid="mobile-search-btn"
            >
              <MagnifyingGlass size={20} className="text-[#5E6D66]" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 md:gap-3">
            {/* Org Selector */}
            <OrgSelector />

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors hidden md:block"
              data-testid="theme-toggle"
              title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            >
              {theme === 'dark' ? (
                <Sun size={20} className="text-amber-400" weight="fill" />
              ) : (
                <Moon size={20} className="text-[#5E6D66]" />
              )}
            </button>

            {/* Desktop create button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  className="bg-[#2D5A4C] hover:bg-[#1E4136] text-white hidden sm:flex"
                  data-testid="quick-create-btn"
                >
                  <Plus size={18} className="mr-2" />
                  Créer
                  <CaretDown size={14} className="ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/contacts/new')} data-testid="create-contact-btn">
                  <Users size={16} className="mr-2" />
                  Nouveau contact
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/organizations/new')} data-testid="create-org-btn">
                  <Buildings size={16} className="mr-2" />
                  Nouvelle organisation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/opportunities/new')} data-testid="create-opp-btn">
                  <Target size={16} className="mr-2" />
                  Nouvelle opportunité
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dossiers/new')} data-testid="create-dossier-btn">
                  <Folder size={16} className="mr-2" />
                  Nouveau dossier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/tasks/new')} data-testid="create-task-btn">
                  <CheckSquare size={16} className="mr-2" />
                  Nouvelle tâche
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile create button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors sm:hidden" data-testid="mobile-create-btn">
                  <Plus size={22} className="text-[#2D5A4C]" weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/contacts/new')}>
                  <Users size={16} className="mr-2" />
                  Nouveau contact
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/organizations/new')}>
                  <Buildings size={16} className="mr-2" />
                  Nouvelle organisation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dossiers/new')}>
                  <Folder size={16} className="mr-2" />
                  Nouveau dossier
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <button
                  className="p-2 hover:bg-[#F9F8F5] rounded-lg transition-colors relative"
                  data-testid="notifications-btn"
                >
                  <Bell size={20} className="text-[#5E6D66]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#D47A62] rounded-full flex items-center justify-center text-[10px] font-bold text-white px-1 animate-pulse" data-testid="notif-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                  <span
                    className={`absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-400' : 'bg-gray-300'}`}
                    data-testid="ws-status-indicator"
                    title={wsConnected ? 'Temps reel actif' : 'Reconnexion...'}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[340px] sm:w-[380px] p-0" data-testid="notif-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E6E1]">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[#1A2421]">Notifications</h3>
                    {wsConnected ? (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium" data-testid="ws-connected-label">
                        <WifiHigh size={12} weight="bold" />
                        Live
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] text-gray-400 font-medium" data-testid="ws-disconnected-label">
                        <WifiSlash size={12} />
                        Hors ligne
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {user?.role === 'super_admin' && (
                      <button
                        onClick={handleRunAutomations}
                        disabled={runningAutomation}
                        className="p-1.5 hover:bg-[#F9F8F5] rounded-md transition-colors text-[#2D5A4C]"
                        title="Lancer le scan automatique"
                        data-testid="run-automations-btn"
                      >
                        <Lightning size={16} weight="fill" className={runningAutomation ? 'animate-pulse' : ''} />
                      </button>
                    )}
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-[#2D5A4C] hover:underline font-medium"
                        data-testid="mark-all-read-btn"
                      >
                        Tout lire
                      </button>
                    )}
                  </div>
                </div>
                <ScrollArea className="max-h-[360px]">
                  {notifications.length === 0 ? (
                    <div className="py-8 text-center">
                      <Bell size={28} className="mx-auto text-[#E8E6E1] mb-2" />
                      <p className="text-sm text-[#5E6D66]">Aucune notification</p>
                    </div>
                  ) : (
                    <div>
                      {notifications.map((notif, idx) => (
                        <div
                          key={`${notif.id}-${idx}`}
                          className={`flex items-start gap-3 px-4 py-3 border-b border-[#F0EFEB] last:border-0 transition-colors cursor-pointer ${
                            !notif.read ? 'bg-[#F9F8F5]' : 'hover:bg-[#FAFAF8]'
                          }`}
                          onClick={() => {
                            if (!notif.read) handleMarkRead(notif.id);
                            if (notif.link) { setNotifOpen(false); navigate(notif.link); }
                          }}
                          data-testid={`notif-item-${notif.id}`}
                        >
                          <div className="mt-0.5">{getSeverityIcon(notif.severity)}</div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs leading-snug ${!notif.read ? 'font-semibold text-[#1A2421]' : 'text-[#5E6D66]'}`}>
                              {notif.title}
                            </p>
                            <p className="text-[11px] text-[#5E6D66] mt-0.5 line-clamp-2">{notif.message}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-[10px] text-[#8DB0A1]">{getTimeAgo(notif.created_at)}</p>
                              {notif.link && (
                                <span className="text-[10px] text-[#2D5A4C] font-medium hover:underline">Voir &rarr;</span>
                              )}
                            </div>
                          </div>
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full bg-[#2D5A4C] mt-1.5 flex-shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        </header>

        {/* Page content - extra bottom padding for mobile bottom nav */}
        <main className="p-3 md:p-6 pb-20 md:pb-6">
          <Breadcrumbs />
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E8E6E1] md:hidden safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-stretch justify-around h-14">
          {[
            { path: '/dashboard', label: 'Accueil', icon: House },
            { path: '/contacts', label: 'Contacts', icon: Users },
            { path: '/dossiers', label: 'Dossiers', icon: Folder },
            { path: '/tasks', label: 'Taches', icon: CheckSquare },
            { path: null, label: 'Plus', icon: List, isMore: true },
          ].map((item) => {
            if (item.isMore) {
              return (
                <button
                  key="more"
                  onClick={() => setMobileMenuOpen(true)}
                  className="flex flex-col items-center justify-center flex-1 gap-0.5 text-[#5E6D66] active:bg-[#F0EFEB] transition-colors"
                  data-testid="bottom-nav-more"
                >
                  <item.icon size={22} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              );
            }
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 gap-0.5 transition-colors active:bg-[#F0EFEB] ${
                  isActive ? 'text-[#2D5A4C]' : 'text-[#5E6D66]'
                }`}
                data-testid={`bottom-nav-${item.path.slice(1)}`}
              >
                <item.icon size={22} weight={isActive ? 'fill' : 'regular'} />
                <span className={`text-[10px] ${isActive ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
                {isActive && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#2D5A4C]" />}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
